import { useBulkUpdateMediaLibFilesMutation } from "@api/media-library";
import Spinner from "@components/ui/spinner";
import {
  MediaLibDialogBase,
  useMediaLibDialogStoreShallow,
} from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";

const DIALOG_ID = "media-lib-dailog-bulk-files-archive";

interface IProps {
  bulkUpdateFiles: (ids: string[], data: Partial<IMediaFile>) => void;
}

export const MediaLibDialogBulkFilesArchiveDialog = ({
  bulkUpdateFiles,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { items, setSelectedItems } = useMediaLibDialogStoreShallow((s) => ({
    items: s.selectedItems,
    setSelectedItems: s.setSelectedItems,
  }));

  const [updateFiles, { isLoading: isSaving }] =
    useBulkUpdateMediaLibFilesMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    try {
      const ids = items
        .filter((el) => el.mediaFrom === "my-library")
        .map((el) => el.id);
      if (ids.length === 0) return;

      const updateData = {
        archived: true,
        folderId: null,
      };

      await updateFiles({
        ids,
        data: updateData,
      }).unwrap();
      bulkUpdateFiles(ids, updateData);
      setSelectedItems([]);
    } catch (e) {
      track("error archiving media files", {
        items,
        error: e,
      });
      toast.error("Error archiving files");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Archive files" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <p className="tw-text-[13px]">
          Are you sure that you want to archive selected files?
        </p>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className={cn(isSaving && "tw-cursor-not-allowed")}
        >
          {isSaving ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Archiving...
            </>
          ) : (
            "Yes, archive"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibDialogBulkFilesArchiveDialog.dialogId = DIALOG_ID;
